import {announce} from '@react-aria/live-announcer'
import {create} from 'mutative'
import {toast} from 'sonner'

import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import {useLocale} from '@/lib/locale/locale'
import {type CartItem, createDefaultCart} from '@/models/cartModel'
import type {FullProduct} from '@/models/productModel'
import queries, {type QueryDataType} from '@/queries/queries'
import {updateCartWith} from '@/services/cartService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

interface addToOrRemoveFromCartVariables {
  items: ({
    variation?: Record<string, string>
  } & CartItem &
    Pick<FullProduct, 'availableQuantity'>)[]
  replace?: boolean
}

enum Action {
  Add,
  Remove,
  Update,
}

export default function useAddToOrRemoveFromCartMutation() {
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const isAuthenticated = useIsAuthenticatedValue()

  return useMutation({
    mutationFn: async (variables: addToOrRemoveFromCartVariables) =>
      updateCartWith({
        isAuthenticated,
        locale,
        items: variables.items,
        replace: variables.replace,
      }),
    onMutate: async variables => {
      await queryClient.cancelQueries({queryKey: queries.getQueryKey('cart', [locale])})

      const previous = queryClient.getQueryData<QueryDataType<'cart'>>(
        queries.getQueryKey('cart', [locale]),
      )

      let action = undefined as Action | undefined

      // Optimistically update to the new value
      await queryClient.setQueryData(
        ...queries.getSetQueryParams('cart', [locale], cart => {
          return create(cart ?? createDefaultCart(), draft => {
            variables.items.forEach(item => {
              const existItemIndex = draft.items.findIndex(i => {
                return i.id === item.id && i.retailer === item.retailer
              })

              if (item.quantity === 0) {
                action = Action.Remove
                if (existItemIndex !== -1) {
                  draft.items.splice(existItemIndex, 1)
                }
              } else if (existItemIndex !== -1) {
                action = variables.replace ? Action.Update : Action.Add
                const existedItem = draft.items[existItemIndex]
                invariant(existedItem, 'existedItem should be defined')

                const newQuantity = variables.replace
                  ? item.quantity
                  : existedItem.quantity + item.quantity

                if (item.availableQuantity && newQuantity > item.availableQuantity) {
                  const leftToAdd = item.availableQuantity - existedItem.quantity
                  if (leftToAdd > 0) {
                    throw new Error(`You can only purchase up to ${leftToAdd} more of this item`)
                  }

                  throw new Error(
                    `You already exceed the maximum of ${item.availableQuantity} for this item`,
                  )
                }

                existedItem.quantity = newQuantity
              } else {
                if (item.availableQuantity && item.quantity > item.availableQuantity) {
                  throw new Error(
                    `You can only purchase up to ${item.availableQuantity} of this item`,
                  )
                }
                action = Action.Add
                draft.items.unshift(item)
              }
            })
          })
        }),
      )

      // Return a context object with the previous value so it can be rolled back when the mutation fails.
      return {
        cart: previous,
        action,
      }
    },
    onSuccess: (data, variables, context) => {
      let message = ''
      switch (context.action) {
        case Action.Add:
          if (variables.items.length < 4) {
            message = `Added to cart: ${variables.items
              .map(item => `${item.title} * ${item.quantity}`)
              .join(', ')}`
          } else {
            message = `${variables.items.reduce(
              (curr, item) => item.quantity + curr,
              0,
            )} items added to cart`
          }
          break
        case Action.Remove:
          if (variables.items.length < 4) {
            message = `Removed from cart: ${variables.items.map(item => item.title).join(', ')}`
          } else {
            message = `${variables.items.reduce(
              (curr, item) => item.quantity + curr,
              0,
            )} items removed from cart`
          }
          break
        case Action.Update:
          message = variables.items
            .map(item => `${item.title}'s quantity updated to ${item.quantity}`)
            .join(', ')
          break
        case undefined:
          break
      }

      announce(message)
      toast.success(message)

      queryClient.setQueryData(...queries.getSetQueryParams('cart', [locale], () => data))
    },
    onError: (err, variables, context) => {
      toastErrorMessage(err.message)

      const newCart = context?.cart

      if (newCart) {
        queryClient.setQueryData(...queries.getSetQueryParams('cart', [locale], () => newCart))
      }
    },
  })
}
